import { ADMIN_STATUS_COLORS } from '@/config/adminChartTheme';
import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import ChartCard from '../ChartCard';
import { fetchCampaignsOverview } from '../../../services/campaignAnalyticsService';
import { TimeInterval } from '../../../types/types';

interface CampaignsOverview {
  totalCampaigns: number;
  activeCampaigns: number;
  statusDistribution: Record<string, number>;
}

interface CampaignStatusDonutProps {
  filter: TimeInterval;
  refreshToken?: number;
}

const CampaignStatusDonut: React.FC<CampaignStatusDonutProps> = ({
  filter,
  refreshToken,
}) => {
  const [data, setData] = useState<CampaignsOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchCampaignsOverview(filter)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [filter, refreshToken]);

  const distribution = data?.statusDistribution ?? {};
  const entries = Object.entries(distribution).filter(([, v]) => v > 0);
  const series = entries.map(([, v]) => v);
  const labels = entries.map(([k]) => k.charAt(0).toUpperCase() + k.slice(1));
  const colors = entries.map(([k]) => ADMIN_STATUS_COLORS[k] ?? '#E6E1FF');

  const options: ApexOptions = {
    chart: {
      type: 'donut',
      toolbar: { show: false },
      fontFamily: 'Inter, sans-serif',
      background: 'transparent',
      animations: { enabled: true, easing: 'easeinout', speed: 500 },
    },
    labels,
    colors,
    legend: {
      position: 'bottom',
      fontSize: '12px',
      labels: { colors: '#6B778C' },
      markers: { shape: 'circle' },
      itemMargin: { horizontal: 8, vertical: 4 },
      onItemHover: { highlightDataSeries: false },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              fontSize: '12px',
              color: '#9B919D',
              formatter: () => String(data?.totalCampaigns ?? 0),
            },
          },
        },
        expandOnClick: false,
      },
    },
    stroke: { width: 3, colors: ['#FCFCFC'] },
    dataLabels: { enabled: false },
    tooltip: {
      theme: 'light',
      y: { formatter: (v) => `${v} campaigns` },
    },
    states: {
      hover: { filter: { type: 'none' } },
      active: { filter: { type: 'none' } },
    },
    responsive: [
      {
        breakpoint: 480,
        options: { legend: { position: 'bottom' } },
      },
    ],
  };

  return (
    <ChartCard
      title="Campaign Status"
      subtitle={`${data?.totalCampaigns ?? 0} total campaigns`}
      loading={loading}
      fillHeight
      className="h-full w-full min-h-0"
    >
      <div className="flex-1 flex flex-col min-h-0 justify-center">
        {series.length > 0 ? (
          <div className="flex justify-center items-center min-h-[280px] flex-1">
            <ReactApexChart
              options={options}
              series={series}
              type="donut"
              height={280}
              width="100%"
            />
          </div>
        ) : (
          <div className="min-h-[280px] flex-1 flex items-center justify-center">
            <p className="text-[#9B919D] text-sm">No campaign data</p>
          </div>
        )}
      </div>
    </ChartCard>
  );
};

export default CampaignStatusDonut;
